import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Accordion, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess } from '../../utils/helpers';
import { IntegrationsMetadata, PartnerInformation } from '../../utils/typings';

interface PartnerInformationAccordionProps {
  partnerInformation?: PartnerInformation;
  integrationsMetadata?: IntegrationsMetadata;
  userRole?: keyof typeof UserRole;
}

const PartnerInformationAccordion = ({
  partnerInformation,
  integrationsMetadata,
  userRole,
}: PartnerInformationAccordionProps) => {
  const hasAccess = useMemo(
    () => checkRbacAccess(integrationsMetadata?.partnerInformation, userRole),
    [integrationsMetadata, userRole]
  );

  const infoFields = useMemo(
    () =>
      [
        ['Partner Name', partnerInformation?.partnerName],
        ['Partner Status', partnerInformation?.partnerStatus],
        ['Partnership Type', partnerInformation?.partnershipType],
        ['Partnership Start Date', partnerInformation?.partnershipStartDate],
        ['Renewal Status', partnerInformation?.renewalStatus],
      ].filter(([, value]) => value !== undefined && value !== null),
    [partnerInformation]
  );

  return (
    <Accordion variant='main' showBoxShadow>
      <Accordion.Item value='partner-info'>
        <Accordion.Header>
          <Text weight='bold'>Partner Information</Text>
        </Accordion.Header>

        <Accordion.Body>
          {!hasAccess ? (
            <Text>No Partner Information available for your role.</Text>
          ) : (
            <>
              <div css={partnerGridStyle}>
                {infoFields.length > 0 ? (
                  infoFields.map(([label, value]) => (
                    <div key={label}>
                      <Text weight='bold'>{label}</Text>
                      <Text>{value || '—'}</Text>
                    </div>
                  ))
                ) : (
                  <Text>No partner metadata available.</Text>
                )}
              </div>

              {partnerInformation?.partnerDetails && (
                <>
                  <Text weight='bold'>Partner Details</Text>
                  <Text>{partnerInformation.partnerDetails}</Text>
                </>
              )}
            </>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default PartnerInformationAccordion;

const partnerGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  align-items: start;
  margin-bottom: 40px;
`;
